/** Links between notes: which note an address names, and pointing links at a renamed note. Pure. */
/** A note, as far as links care: its file name and whether it becomes a page on the site. */
export interface NoteRef {
  path: string;
  isThought: boolean;
}

/** The file name without `.md`: what the site calls the page. */
export function stemOf(path: string): string {
  return path.replace(/\.md$/i, "");
}

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** An address as written in Markdown, without `<>` and with `%20` and friends turned back. */
function decodeAddress(raw: string): string {
  const text = raw.startsWith("<") && raw.endsWith(">") ? raw.slice(1, -1) : raw;
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function pickByName<T extends { path: string }>(
  list: T[],
  wanted: string,
  nameOf: (path: string) => string,
): T | null {
  const exact = list.find((n) => nameOf(n.path) === wanted);
  if (exact) return exact;
  const lower = wanted.toLowerCase();
  const loose = list.filter((n) => nameOf(n.path).toLowerCase() === lower);
  return loose.length === 1 ? loose[0]! : null;
}

/**
 * The note an address points at, from a note of this kind. A Document links by file name
 * (`garden-notes.md`); a Thought links by its page on the site (`/welcome-to-quire`), and only to
 * Thoughts. Nested folders are not handled, so an address with another `/` means nothing here.
 * Capitals are forgiven when there is no ambiguity. `anchor` is the part after `#`, without it.
 */
export function resolveNoteLink(
  address: string,
  fromThought: boolean,
  notes: NoteRef[],
): { path: string; anchor: string } | null {
  const decoded = decodeAddress(address.trim()).trim();
  if (!decoded || decoded.startsWith("#") || decoded.startsWith("//") || SCHEME.test(decoded)) {
    return null;
  }
  const hash = decoded.indexOf("#");
  const target = hash < 0 ? decoded : decoded.slice(0, hash);
  const anchor = hash < 0 ? "" : decoded.slice(hash + 1);
  if (fromThought) {
    if (!target.startsWith("/")) return null;
    const slug = target.slice(1);
    if (!slug || slug.includes("/")) return null;
    const hit = pickByName(
      notes.filter((n) => n.isThought),
      slug,
      stemOf,
    );
    return hit ? { path: hit.path, anchor } : null;
  }
  if (target.includes("/") || !/\.md$/i.test(target)) return null;
  const hit = pickByName(notes, target, (p) => p);
  return hit ? { path: hit.path, anchor } : null;
}

/** Whether an address is written the way a note link is, even if no note has that name. */
export function looksLikeNoteLink(address: string, fromThought: boolean): boolean {
  const text = decodeAddress(address.trim()).trim();
  if (!text || text.startsWith("#") || text.startsWith("//") || SCHEME.test(text)) return false;
  const target = text.split("#")[0]!;
  return fromThought ? /^\/[^/]+$/.test(target) : /^[^/]+\.md$/i.test(target);
}

const LINK = /\]\((<[^>\n]*>|[^)\s]*)(\s+"[^"]*")?\)/g;
const FENCE = /^ {0,3}(`{3,}|~{3,})/;

/** Writes a file name into an address, the way the address it replaces was written. */
function encodeAs(address: string, like: string): string {
  return like.startsWith("<") ? `<${address}>` : address.replace(/ /g, "%20");
}

/**
 * Points links at a renamed note. Only links written in the form their note uses are changed:
 * `/old` in a Thought (when the renamed note is a Thought), `old.md` in a Document. The match is
 * exact, any `#heading` and link title stay, and so do links inside code and the front matter.
 */
export function rewriteLinks(
  text: string,
  fromThought: boolean,
  oldPath: string,
  newPath: string,
  renamedIsThought: boolean,
): { text: string; count: number } {
  if (fromThought && !renamedIsThought) return { text, count: 0 };
  const from = fromThought ? `/${stemOf(oldPath)}` : oldPath;
  const to = fromThought ? `/${stemOf(newPath)}` : newPath;
  let count = 0;
  const lines = text.split("\n");
  let fence: string | null = null;
  let inFront = lines[0]?.replace(/^\uFEFF/, "").trimEnd() === "---";
  const fix = (piece: string) =>
    piece.replace(LINK, (whole, raw: string, title: string | undefined) => {
      const decoded = decodeAddress(raw);
      const hash = decoded.indexOf("#");
      const target = hash < 0 ? decoded : decoded.slice(0, hash);
      if (target !== from) return whole;
      count += 1;
      const anchor = hash < 0 ? "" : decoded.slice(hash);
      return `](${encodeAs(to + anchor, raw)}${title ?? ""})`;
    });
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;
    if (i === 0 && inFront) continue;
    if (inFront) {
      if (line.trimEnd() === "---" || line.trimEnd() === "...") inFront = false;
      continue;
    }
    const open = FENCE.exec(line);
    if (fence) {
      if (open && open[1]![0] === fence[0] && open[1]!.length >= fence.length) fence = null;
      continue;
    }
    if (open) {
      fence = open[1]!;
      continue;
    }
    // Inline code is left as it is: only the pieces between code spans are looked at.
    lines[i] = line
      .split(/(`+[^`]*`+)/)
      .map((piece, n) => (n % 2 === 1 ? piece : fix(piece)))
      .join("");
  }
  return { text: lines.join("\n"), count };
}
